import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Check, Messages, Star } from '@saleshandy/icons';
import { IProps } from './types';
import Button from '../../../../../../../../shared/design-system/components/atoms/button';
import {
  getButtonLabel,
  getInboxAnalyzerPlanMonthlyPrice,
  getInboxAnalyzerPlanQuarterlyPrice,
  getInboxAnalyzerPlanYearlyPrice,
  getPlanDetailsObj,
  getPlanPrice,
  getPlansIcon,
} from './utils/helper';
import { SelectPlanCTAText } from '../../../../../../../../shared/design-system/components/molecules/pricing-card/types';
import { OverlayTooltip } from '../../../../../../../../shared/design-system/components/overlay';
import { isAnnuallyPlan } from '../../utils/helper';
import { isUserInPaidPlanInInboxAnalyzer } from '../../../../../../../../shared/design-system/components/molecules/subscription-plan-details/helper/helper';
import { isQuarterlyPlan } from '../../../outreach-plans/utils/helper';
import GlobalRestrictionModal from '../../../../../../../home/components/global-restriction-modal';

const InboxRadarPlanCard: React.FC<IProps> = ({
  plan,
  // customScalePlusPlans,
  isPurchaseInProgress,
  handlePlanSelect,
  sendGetInboxAnalyzerSubscriptionResponse,
  sendGetInboxAnalyzerPlansResponse,
  planCycle,
  isDemoAccount,
}) => {
  const { t } = useTranslation();

  //* State */

  const isUserSubscribed = isUserInPaidPlanInInboxAnalyzer(
    sendGetInboxAnalyzerSubscriptionResponse?.planCode,
  );

  const [planDetails, setPlanDetails] = useState(
    getPlanDetailsObj(plan, isUserSubscribed),
  );

  const [showGlobalRestrictionModal, setShowGlobalRestrictionModal] = useState(
    false,
  );

  const btnLabel = getButtonLabel({
    plan: planDetails,
    sendGetInboxAnalyzerSubscriptionResponse,
    sendGetInboxAnalyzerPlansResponse,
    planCycle,
  });

  const getButtonLabelTxt = () => {
    if (planDetails?.displayName === 'Custom') {
      return SelectPlanCTAText.CustomPlan;
    }

    return btnLabel;
  };

  const getButtonTheme = () => {
    if (
      planDetails?.isBestValuePlan &&
      btnLabel !== SelectPlanCTAText.CurrentPlan &&
      btnLabel !== SelectPlanCTAText.DowngradePlan
    ) {
      return Button.Theme.Solid;
    }

    return Button.Theme.Ghost;
  };

  const getPlanDiscountText = () => {
    if (isAnnuallyPlan(planCycle)) {
      return `Save $${
        (getInboxAnalyzerPlanMonthlyPrice(
          planDetails.planCode,
          sendGetInboxAnalyzerPlansResponse,
        ) -
          planDetails.price) *
        planDetails?.planMode
      } annually`;
    }
    if (isQuarterlyPlan(planCycle)) {
      return `Save $${
        (getInboxAnalyzerPlanQuarterlyPrice(
          planDetails.planCode,
          sendGetInboxAnalyzerPlansResponse,
        ) -
          planDetails.price) *
        planDetails?.planMode
      } annually`;
    }
    return `Save $${
      (planDetails.price -
        getPlanPrice(
          getInboxAnalyzerPlanYearlyPrice(
            planDetails.planCode,
            sendGetInboxAnalyzerPlansResponse,
          ),
          12,
        )) *
      12
    } on annual plan`;
  };

  //* Class Variable */
  const cardClassName = classnames([
    'pricing-card',
    {
      'best-value-plan': planDetails?.isBestValuePlan,
    },
  ]);

  const btnClassName = classnames([
    {
      'ghost-primary-new': !planDetails?.isBestValuePlan,
    },
    {
      'ghost-primary-new-disable': btnLabel === SelectPlanCTAText.DowngradePlan,
    },
    {
      'solid-primary-new':
        btnLabel !== SelectPlanCTAText.CurrentPlan &&
        btnLabel !== SelectPlanCTAText.DowngradePlan &&
        planDetails?.isBestValuePlan,
    },
    {
      'ghost-secondary-new-disable': btnLabel === SelectPlanCTAText.CurrentPlan,
    },
  ]);

  useEffect(() => {
    if (plan) {
      setPlanDetails(getPlanDetailsObj(plan, isUserSubscribed));
    }
  }, [plan, isUserSubscribed]);

  return (
    <div className={cardClassName}>
      <div className="best-value-plan__labels-wrappers">
        {planDetails?.isBestValuePlan && (
          <div className="best-value-plan__labels">
            <Star color="#FFFFFF" />
            <span className="best-value-plan__labels-text">
              {t('labels.most_popular')}
            </span>
          </div>
        )}
      </div>
      <div className="out-reach-plans-card">
        <div className="card-body">
          <div className="pricing-card__details mb-2">
            <div className="pricing-card__details-header">
              <img src={getPlansIcon(planDetails?.planName)} alt="building" />
              <span className="pricing-card__details-header-title">
                {planDetails?.displayName}
              </span>
            </div>
            <div className="pricing-card__details-price-wrapper">
              {planDetails?.price ? (
                <>
                  <div className="pricing-card__details-price-wrapper-currency">
                    <span className="pricing-card__details-price-wrapper-currency-amount">
                      ${Math.round(planDetails?.price)}
                    </span>
                    {(isAnnuallyPlan(planCycle) ||
                      isQuarterlyPlan(planCycle)) && (
                      <span className="pricing-card__details-price-wrapper-currency-amount-strikethrough">
                        $
                        {getInboxAnalyzerPlanMonthlyPrice(
                          planDetails?.planCode,
                          sendGetInboxAnalyzerPlansResponse,
                        )}
                      </span>
                    )}
                    <span className="pricing-card__details-price-wrapper-currency-amount-suffix">
                      /Month
                    </span>
                  </div>
                  <div className="pricing-card__details-price-wrapper-discount">
                    <span className="pricing-card__details-price-wrapper-discount-text">
                      {getPlanDiscountText()}
                    </span>
                  </div>
                </>
              ) : (
                <div className="mt-1">
                  <Messages
                    width={28}
                    height={28}
                    className="popover-arrow-color-txt"
                  />
                </div>
              )}
            </div>

            <Button
              variant={Button.Variant.Primary}
              theme={getButtonTheme()}
              onClick={() => {
                if (isDemoAccount) {
                  setShowGlobalRestrictionModal(true);
                  return;
                }
                handlePlanSelect(planDetails?.id, planDetails?.planName);
              }}
              className={`w-100 ${btnClassName}`}
              isLoading={isPurchaseInProgress}
              disabled={
                btnLabel === SelectPlanCTAText.CurrentPlan ||
                btnLabel === SelectPlanCTAText.DowngradePlan ||
                isPurchaseInProgress
              }
            >
              {getButtonLabelTxt()}
            </Button>
          </div>

          <div className="pricing-card__features">
            <ul className="pricing-card__features-list">
              {planDetails?.features?.map(
                ({
                  label,
                  labelClass,
                  tooltipText,
                  strikeText,
                  highlight,
                  tooltipClassName,
                }) => (
                  <li
                    key={label}
                    className={classnames([
                      'pricing-card__features-list-item',
                      {
                        'bg-color': highlight,
                      },
                    ])}
                  >
                    <span className="pricing-card__features-list-item-check">
                      <Check className="check-icon-email-outreach" />
                    </span>
                    <>
                      {tooltipText ? (
                        <OverlayTooltip
                          text={tooltipText}
                          className={tooltipClassName}
                        >
                          <span
                            className={classnames([
                              'pricing-card__features-list-item-label',
                              'underline-dashed',
                              labelClass,
                            ])}
                          >
                            {strikeText && (
                              <span className="regular-1 gray-txt-15 mr-1 text-decoration-line-through">
                                {strikeText}
                              </span>
                            )}
                            {label}
                          </span>
                        </OverlayTooltip>
                      ) : (
                        <span
                          className={classnames([
                            'pricing-card__features-list-item-label',
                            labelClass,
                          ])}
                        >
                          {strikeText && (
                            <span className="regular-1 gray-txt-15 mr-1 text-decoration-line-through">
                              {strikeText}
                            </span>
                          )}
                          {label}
                        </span>
                      )}
                    </>
                  </li>
                ),
              )}
            </ul>
          </div>
        </div>
      </div>
      <GlobalRestrictionModal
        show={showGlobalRestrictionModal}
        onClose={() => setShowGlobalRestrictionModal(false)}
        title="You’re about to leave the demo account"
        content="Upgrading a plan requires a real Saleshandy account. You’ll be redirected to log in or sign up and continue from your actual account. Do you want to proceed?"
        redirectTo="billing/upgrade-plan/inbox-placement-radar"
      />
    </div>
  );
};

export default InboxRadarPlanCard;
